import { z } from 'zod';
import { StandardRaceSchema } from './race';

/**
 * Definizione di un Archetipo nella ClassDefinition.
 *
 * **Tutte e 12 le classi DWR hanno archetipi.** Un archetipo è una scelta
 * in fase di creazione personaggio (separata e successiva alla scelta della
 * razza) che concede al PG una mossa speciale (`class_special` nel catalogo
 * Move). L'archetipo NON è la razza: è il "vantaggio meccanico" che la
 * scheda PG associa per default alla razza tipica di quel ruolo.
 *
 * Distinzione strutturale tra le 12 classi:
 *
 * - **11 classi** hanno **5 archetipi razza-tematici** (Umano, Nano, Elfo,
 *   Halfling, Mezzorco): l'archetipo è la manifestazione meccanica
 *   tipicamente associata a quella razza per quella classe. `associatedRace`
 *   è valorizzato.
 * - **Il Barbaro** è l'eccezione: ha **3 archetipi origine-tematici**
 *   (Straniero, Selvaggio, Nomade), NON razza-tematici — per il Barbaro la
 *   razza non determina l'archetipo. `associatedRace` è undefined.
 *
 * Conseguenza: a livello concettuale, "*è il Barbaro a non avere il legame
 * razza→archetipo, non le altre classi a non avere archetipo*".
 *
 * **Razza e Archetipo sono comunque scelte INDIPENDENTI nel PG**: la mappa
 * razza→archetipo (es. "Elfo del Mago") è una **convenzione di design** per
 * guidare la creazione personaggio attorno a stereotipi narrativi. NON è
 * un vincolo: un PG di razza Nano può legittimamente scegliere l'archetipo
 * "Elfo" della sua classe e ottenerne il bonus meccanico. Il manuale lo
 * esplicita («scegli pure l'archetipo che preferisci»).
 *
 * Conseguenza per la scheda PG: `race` e `archetypeId` sono campi separati
 * e potenzialmente disallineati. La UI può **suggerire** l'allineamento
 * (es. "il PG è Nano → archetipo Nano consigliato") ma non deve forzarlo.
 *
 * L'effetto dell'archetipo (incluso il caso "guadagni un incantesimo da
 * un'altra Tradizione") è sempre veicolato da una mossa nel catalogo:
 * `grantedMoveId` punta a quella mossa.
 */
export const ArchetypeSchema = z.object({
  /** ID stabile entro la classe. Es. 'umano', 'straniero'. */
  id: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  /**
   * Razza associata all'archetipo per **CONVENZIONE** di design (NON vincolo).
   * Vedi nota in testa al file: il giocatore può scegliere questo archetipo
   * indipendentemente dalla razza del proprio PG.
   *
   * Undefined per il Barbaro (3 archetipi su origine/background).
   */
  associatedRace: StandardRaceSchema.optional(),
  /** Descrizione narrativa breve dell'archetipo. */
  description: z.string().min(1),
  /**
   * ID della mossa speciale concessa dall'archetipo.
   * Punta a una entry del catalogo Move con `type='class_special'`.
   */
  grantedMoveId: z.string().min(1),
  /**
   * **FI-1**: variazione del valore `baseHp` di classe per questo archetipo.
   *
   * Alcuni archetipi modificano statistiche di classe in modo strutturale
   * (non solo descrittivo). L'unico caso noto al momento è il
   * **Paladino-Nano** (manuale V1, r. 4846): «i tuoi PF massimi sono
   * 12+Costituzione» — quindi `baseHp` passa da 10 (standard Paladino) a 12.
   *
   * Quando definito, `baseHpOverride` sostituisce `ClassDefinition.baseHp` per
   * il calcolo dei PF massimi del PG (`hp.max = baseHpOverride + COS_punteggio`).
   * Vedi `services/characterCreation.ts::deriveStartingDerivedStats`.
   *
   * Default `undefined` = usa il `baseHp` della classe (caso standard).
   *
   * **Additivo**: nessun bump di `CHARACTER_SCHEMA_VERSION` — il campo è
   * opzionale e non cambia la rappresentazione del Character finale.
   */
  baseHpOverride: z.number().int().positive().optional(),
  /**
   * **Audit 1 (D11 follow-up)**: selezioni di scelta su mosse di setup
   * concesse automaticamente dall'archetipo, in aggiunta alle scelte manuali
   * del giocatore.
   *
   * Caso d'uso: **Druido-Elfo** (manuale V1, r. 3039): «Considera sempre le
   * Grandi Foreste come una tua Terra, **in aggiunta** a quella che scegli
   * con *Nato dalla Terra*». L'archetipo aggiunge la choice `grandi-foreste`
   * di `druido.nato-dalla-terra` al pool effettivo del PG senza consumare
   * il `choiceCount` della mossa (il giocatore sceglie comunque 1 Terra
   * manualmente; chi è Druido-Elfo ne ha 2 effettive).
   *
   * I consumer (es. mosse runtime che dipendono dal pool Terre del PG)
   * usano l'helper SSOT `getEffectiveSelectedChoices(character, moveId)`
   * (in `services/archetypeGrants.ts`) per unire scelte manuali +
   * auto-grantate, deduplicandole.
   *
   * Campo additivo opzionale: nessun bump di `CHARACTER_SCHEMA_VERSION`.
   */
  grantsMoveSelections: z
    .array(
      z.object({
        moveId: z.string().min(1),
        choiceIds: z.array(z.string().min(1)).min(1),
      }),
    )
    .optional(),
});
export type Archetype = z.infer<typeof ArchetypeSchema>;
